import React, { useState } from "react";
import {
  Button,
  Search,
  Grid,
  Column,
  Loading,
  Tag,
  Theme,
} from "@carbon/react";
import { FormattedMessage, useIntl } from "react-intl";
import SearchOutput from "./searchOutput";
import {
  fetchPatientData,
  useAutocomplete,
  type PatientSearchResult,
} from "./searchService";
import "./searchBar.css";

const SearchBar: React.FC = () => {
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [patientData, setPatientData] = useState<PatientSearchResult[]>([]);
  const intl = useIntl();
  const {
    textValue,
    onChange: handleAutocompleteChange,
    onKeyDown: handleAutocompleteKeyDown,
    setTextValue,
  } = useAutocomplete({
    value: searchInput,
    suggestions: [],
    allowFreeText: true,
    onDelete: (id) => {
      setPatientData((prevData) =>
        prevData.filter((patient) => patient.patientID !== id),
      );
    },
  });

  const handleClearSearch = () => {
    setSearchInput("");
    setTextValue("");
    setPatientData([]);
  };

  const handleSearch = () => {
    if (searchInput.trim()) {
      setLoading(true);
      fetchPatientData(searchInput.trim(), (results) => {
        const uniqueResults = results.filter(
          (result, index, self) =>
            result.patientID &&
            result.firstName &&
            result.lastName &&
            index === self.findIndex((t) => t.patientID === result.patientID),
        );
        setPatientData(uniqueResults);
        setLoading(false);
      });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const userInput = e?.target?.value || "";
    setSearchInput(userInput);
    handleAutocompleteChange(e);
    setLoading(true);

    if (userInput.trim()) {
      fetchPatientData(userInput.trim(), (results) => {
        const uniqueResults = results.filter(
          (result, index, self) =>
            result.patientID &&
            result.firstName &&
            result.lastName &&
            index === self.findIndex((t) => t.patientID === result.patientID),
        );
        setPatientData(uniqueResults);
        setLoading(false);
      });
    } else {
      setPatientData([]);
      setLoading(false);
    }
  };

  return (
    <Grid className="main">
      <Column sm={4} md={8} lg={16}>
        <div className="search-bar-container">
          {/* Theme wrapper ONLY around Search input to make it light */}
          <Theme theme="white">
            <Search
              size="sm"
              placeholder={intl.formatMessage({ id: "label.button.search" })}
              labelText={intl.formatMessage({ id: "label.button.search" })}
              closeButtonLabelText={intl.formatMessage({
                id: "label.button.clear",
              })}
              id="searchItem"
              value={textValue}
              onChange={handleChange}
              onKeyDown={handleAutocompleteKeyDown}
              onClear={handleClearSearch}
              className="search-input"
              autoComplete="on"
            />
          </Theme>
          <Button
            id="patientSearch"
            size="sm"
            style={{ width: 50 }}
            onClick={handleSearch}
            aria-label={intl.formatMessage({ id: "label.button.search" })}
          >
            <FormattedMessage id="label.button.search" />
          </Button>
        </div>
      </Column>

      <Column sm={4} md={8} lg={16}>
        {(loading || patientData.length > 0) && (
          <div className="patients">
            {loading ? (
              <Loading
                description={intl.formatMessage({ id: "label.loading" })}
                withOverlay={false}
              />
            ) : (
              <>
                <div>
                  <em
                    style={{
                      fontFamily: "serif",
                      color: "#000",
                      marginLeft: "10px",
                    }}
                  >
                    <FormattedMessage id="sidenav.label.results" />:
                  </em>{" "}
                  <Tag size="sm" type="blue">
                    {patientData.length}
                  </Tag>
                </div>
                <SearchOutput
                  loading={loading}
                  patientData={patientData}
                  onSelect={handleClearSearch}
                />
              </>
            )}
          </div>
        )}
      </Column>
    </Grid>
  );
};

export default SearchBar;
